/**
 * Общее для страниц базы знаний: запросы, подписи, безопасный вывод простого текста (заголовки, списки, жирный)
 * и сравнение двух версий построчно.
 */
import type { KbArticleItem, KbReplacedPart } from '@epc/contracts';
import { queryOptions } from '@tanstack/react-query';
import clsx from 'clsx';
import { Fragment, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { Badge, type Tone } from '../../ui/feedback';

export interface KbMeta {
  sections: Array<{ code: string; title: string }>;
  pilot: { available: boolean };
  semanticSearch: boolean;
  ocr: { available: boolean; note: string | null };
  canEdit: boolean;
  isAdmin: boolean;
  roles: string[];
  allRoles: Array<{ code: string; name: string }>;
}

export const metaQuery = queryOptions({ queryKey: ['kb', 'meta'], queryFn: () => api<KbMeta>('/kb/meta') });

export const sectionTitle = (meta: KbMeta | undefined, code: string) =>
  meta?.sections.find((s) => s.code === code)?.title ?? code;

export const VERSION_TONE: Record<string, Tone> = {
  draft: 'neutral',
  review: 'warning',
  published: 'success',
  superseded: 'neutral',
  rejected: 'danger',
};
export const VERSION_TITLE: Record<string, string> = {
  draft: 'Черновик',
  review: 'На проверке',
  published: 'Действует',
  superseded: 'Заменена новой',
  rejected: 'Возвращена',
};

export function ArticleBadges({
  a,
}: {
  a: Pick<KbArticleItem, 'status' | 'replaced' | 'pendingReview' | 'reviewOverdue'>;
}) {
  return (
    <span className="inline-flex flex-wrap gap-1.5">
      {a.status === 'draft' ? <Badge tone="neutral">Не опубликована</Badge> : null}
      {a.status === 'archived' ? <Badge tone="neutral">В архиве</Badge> : null}
      {a.replaced ? <Badge tone="warning">Есть заменённое</Badge> : null}
      {a.pendingReview ? <Badge tone="info">Ждёт проверки</Badge> : null}
      {a.reviewOverdue ? <Badge tone="danger">Пора пересмотреть</Badge> : null}
    </span>
  );
}

/** Найденные слова в ответе поиска обрамлены ⟦ ⟧: выводим <mark> без разметки из текста */
export function Snippet({ text }: { text: string }) {
  const parts = text.split(/(⟦[^⟧]*⟧)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('⟦') ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: части одного фрагмента не переставляются
          <mark key={i} className="rounded bg-accent/30 px-0.5 text-ink">
            {p.slice(1, -1)}
          </mark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: части одного фрагмента не переставляются
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}

/* ───────────── Простой текст статьи ───────────── */

function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**') && p.length > 4 ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: части одной строки
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: части одной строки
      <Fragment key={i}>{p}</Fragment>
    ),
  );
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Заголовки (#), списки (- или 1.), абзацы и **жирный**; остальное выводится как текст, разметка HTML не
 * выполняется. Заменённые части (БЗ-11) помечены и приглушены.
 */
export function ArticleText({
  body,
  replaced = [],
  replacerLabel,
}: {
  body: string;
  replaced?: KbReplacedPart[];
  replacerLabel?: (id: string) => string;
}) {
  const lines = body.split('\n');
  const out: ReactNode[] = [];
  let list: string[] = [];
  let ordered = false;
  // Пока идёт заменённый раздел, его уровень запоминается: вложенные заголовки и текст приглушаются
  let dimLevel = 0;
  let dim = false;
  const flush = (key: string) => {
    if (!list.length) return;
    const items = list.map((t) => <li key={`${key}-${t}`}>{inline(t)}</li>);
    out.push(
      ordered ? (
        <ol key={key} className={clsx('my-2 list-decimal pl-6', dim && 'opacity-60')}>
          {items}
        </ol>
      ) : (
        <ul key={key} className={clsx('my-2 list-disc pl-6', dim && 'opacity-60')}>
          {items}
        </ul>
      ),
    );
    list = [];
  };
  lines.forEach((raw, idx) => {
    const key = `l${idx}`;
    const h = /^(#{1,6})\s+(.*\S)\s*$/.exec(raw);
    const li = /^\s*(?:[-*]|\d+[.)])\s+(.*\S)\s*$/.exec(raw);
    if (li) {
      const isOrdered = /^\s*\d/.test(raw);
      if (list.length && ordered !== isOrdered) flush(`${key}f`);
      ordered = isOrdered;
      list.push(li[1]!);
      return;
    }
    flush(`${key}f`);
    if (h) {
      const level = h[1]!.length;
      const label = norm(h[2]!);
      const part = replaced.find((p) => norm(p.label) === label || label.startsWith(norm(p.label)));
      if (part) {
        dimLevel = level;
        dim = true;
      } else if (dim && level <= dimLevel) dim = false;
      const cls = clsx(
        'mt-5 mb-2 font-semibold text-ink [overflow-wrap:anywhere]',
        level === 1 ? 'text-[20px]' : level === 2 ? 'text-[17px]' : 'text-[15px]',
        dim && 'opacity-60',
      );
      const Tag = `h${Math.min(level + 1, 6)}` as 'h2';
      out.push(
        <Tag key={key} className={cls}>
          {h[2]}
          {part ? (
            <span className="ml-2 inline-block align-middle">
              <Badge tone="warning">
                заменено{replacerLabel ? ` → ${replacerLabel(part.replacedById)}` : ''} с{' '}
                {part.since.split('-').reverse().join('.')}
              </Badge>
            </span>
          ) : null}
        </Tag>,
      );
      return;
    }
    if (!raw.trim()) return;
    out.push(
      <p key={key} className={clsx('my-2 leading-6 [overflow-wrap:anywhere]', dim && 'opacity-60')}>
        {inline(raw)}
      </p>,
    );
  });
  flush('end');
  return <div className="text-[15px] text-ink">{out}</div>;
}

/* ───────────── Сравнение версий ───────────── */

export type DiffRow = { kind: 'same' | 'add' | 'del'; text: string };

/** Построчное сравнение по наибольшей общей подпоследовательности */
export function diffLines(a: string, b: string): DiffRow[] {
  const x = a.split('\n');
  const y = b.split('\n');
  const n = x.length;
  const m = y.length;
  if (n * m > 4_000_000)
    return [
      ...x.map((t) => ({ kind: 'del' as const, text: t })),
      ...y.map((t) => ({ kind: 'add' as const, text: t })),
    ];
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i]![j] = x[i] === y[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      rows.push({ kind: 'same', text: x[i]! });
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) rows.push({ kind: 'del', text: x[i++]! });
    else rows.push({ kind: 'add', text: y[j++]! });
  }
  while (i < n) rows.push({ kind: 'del', text: x[i++]! });
  while (j < m) rows.push({ kind: 'add', text: y[j++]! });
  return rows;
}

export function DiffView({ rows }: { rows: DiffRow[] }) {
  const changed = rows.filter((r) => r.kind !== 'same').length;
  if (!changed) return <p className="text-[14px] text-ink-2">Тексты одинаковые.</p>;
  return (
    <div className="overflow-x-auto rounded-md border border-line text-[13px]">
      {rows.map((r, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: построчное сравнение, порядок фиксирован
          key={i}
          className={clsx(
            'flex gap-2 border-b border-line/60 px-3 py-1 last:border-b-0 [overflow-wrap:anywhere]',
            r.kind === 'add' && 'bg-success-soft text-ink',
            r.kind === 'del' && 'bg-danger-soft text-ink line-through decoration-danger/50',
            r.kind === 'same' && 'text-ink-2',
          )}
        >
          <span aria-hidden className="w-3 shrink-0 font-mono">
            {r.kind === 'add' ? '+' : r.kind === 'del' ? '−' : ''}
          </span>
          <span className="min-w-0 whitespace-pre-wrap">{r.text || ' '}</span>
        </div>
      ))}
    </div>
  );
}
